import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { showToast } from '@/lib/toast';
import { deleteBanner, setBannerActive, type BannerRow } from '@/api/banners';
import { TileMenu } from '@/components/TileMenu';
import { describeSegment } from './audience';

/**
 * Плитка баннера — раскрывающаяся, как у объявлений и шаблонов соглашения.
 * В раскрытии показываются медиафайл, описание, ссылка и правила показа
 * («Кому показывать») — чтобы не заходить в форму ради просмотра.
 */
export function BannerTile({
  banner,
  expanded,
  onToggleExpand,
  onEditRequest,
  onDeleted,
  onPatched,
}: {
  banner: BannerRow;
  expanded: boolean;
  onToggleExpand: () => void;
  onEditRequest: () => void;
  onDeleted: () => void;
  onPatched: (updated: BannerRow) => void;
}) {
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useConfirmDialog();

  const handleDelete = async () => {
    const ok = await confirm(
      `Удалить баннер «${banner.name}»? После удаления все данные будут удалены безвозвратно.`,
      { confirmLabel: 'Удалить', danger: true },
    );
    if (!ok) return;
    setBusy(true);
    try {
      await deleteBanner(banner.id);
      showToast('Баннер удалён');
      onDeleted();
    } catch {
      showToast('Не удалось удалить баннер.');
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      const updated = await setBannerActive(banner.id, !banner.active);
      onPatched(updated);
      showToast(updated.active ? 'Баннер включён' : 'Баннер отключён');
    } catch {
      showToast('Не удалось изменить показ баннера.');
    } finally {
      setBusy(false);
    }
  };

  const period =
    banner.dateFrom || banner.dateTo
      ? `${formatDate(banner.dateFrom)} — ${formatDate(banner.dateTo)}`
      : 'Период не задан';

  return (
    <div className={`catalog-tile${expanded ? ' catalog-tile--expanded' : ''}`}>
      <div className="catalog-tile__main">
        <div className="catalog-tile__icon">
          <Icon name="image" size={18} />
        </div>
        <button type="button" className="catalog-tile__body catalog-tile__body--clickable" onClick={onToggleExpand}>
          <div className="catalog-tile__name">
            {banner.name}
            <span className={`badge ${banner.active ? 'badge--good' : 'badge--neutral'}`}>
              {banner.active ? 'Включён' : 'Отключён'}
            </span>
          </div>
          <div className="catalog-tile__category">{period}</div>
        </button>

        <TileMenu
          disabled={busy}
          active={banner.active}
          onEdit={onEditRequest}
          onDelete={handleDelete}
          onToggleActive={toggleActive}
        />

        <button
          type="button"
          className="catalog-tile__expand"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={expanded ? `Скрыть «${banner.name}»` : `Показать «${banner.name}»`}
        >
          <Icon name="chevronDown" size={16} />
        </button>
      </div>

      {expanded && (
        <div className="catalog-tile__reports">
          <div className="ad-preview">
            {banner.media && (
              <div className="ad-preview__thumb">
                {banner.media.mime?.startsWith('video/') ? (
                  <video src={banner.media.url ?? undefined} controls />
                ) : (
                  <img
                    src={banner.media.url ?? undefined}
                    alt={banner.media.alt ?? banner.media.name ?? 'Медиафайл баннера'}
                  />
                )}
              </div>
            )}
            <div className="ad-preview__text">
              {banner.hint && <p className="banner-detail__hint">{banner.hint}</p>}
              {banner.description ? (
                <p className="banner-detail__description">{banner.description}</p>
              ) : (
                <p className="cell-muted">Описание не задано.</p>
              )}
              {banner.url && (
                <a className="banner-detail__link" href={banner.url} target="_blank" rel="noreferrer">
                  {banner.url}
                </a>
              )}
            </div>
          </div>

          <div className="banner-detail__audience">
            <span className="banner-detail__audience-title">Кому показывать</span>
            {banner.segments.length === 0 ? (
              <p className="cell-muted">Правила не заданы — баннер никто не увидит.</p>
            ) : (
              <ul className="banner-detail__audience-list">
                {banner.segments.map((segment, index) => (
                  <li key={index}>
                    <span className="badge badge--info">{describeSegment(segment)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {dialog}
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const [year, month, day] = value.split('-');
  return `${day}.${month}.${year}`;
}
